import { LISTING_ACTION_MAX_DAYS } from './dto/listing-action'
import { addCalendarDays, viennaToday } from './lunch-menu'

const VIENNA = 'Europe/Vienna'
export const FAVORITE_PUSH_FIRST_HOUR = 8
export const FAVORITE_PUSH_END_HOUR = 22

let wallClock: Intl.DateTimeFormat | null = null

function viennaWall(at: number): Record<string, string> {
  wallClock ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: VIENNA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  return Object.fromEntries(
    wallClock.formatToParts(new Date(at)).map((part) => [part.type, part.value]),
  )
}

function viennaOffsetMs(at: number): number {
  const p = viennaWall(at)
  const wall = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  )
  return wall - Math.floor(at / 1000) * 1000
}

/** The instant a wall-clock date and time in Vienna ("2026-10-13", "19:00") names. */
export function viennaInstant(date: string, time: string): Date {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number)
  const [hour = 0, minute = 0, second = 0] = time.split(':').map(Number)
  const wall = Date.UTC(year, month - 1, day, hour, minute, second)
  // The offset at the naive guess can differ from the one at the result around a time switch,
  // so it is read twice: first at the wall time, then at the corrected guess.
  return new Date(wall - viennaOffsetMs(wall - viennaOffsetMs(wall)))
}

/** The latest end an action sent at `now` may have: the last second of the sixth day after today. */
export function latestActionEnd(now: Date): Date {
  return viennaInstant(
    addCalendarDays(viennaToday(now).date, LISTING_ACTION_MAX_DAYS - 1),
    '23:59:59',
  )
}

export function isActionEndAllowed(endsAt: Date, now: Date): boolean {
  return endsAt.getTime() > now.getTime() && endsAt.getTime() <= latestActionEnd(now).getTime()
}

/** Whether an action still shows: before its end and not ended early. */
export function isActionRunning(
  action: { endsAt: string; endedAt?: string | null },
  now: Date,
): boolean {
  return !action.endedAt && new Date(action.endsAt).getTime() > now.getTime()
}

/** Whether favourite pushes may leave at `now`: 08:00 to 21:59 on a clock in Vienna. */
export function favoritePushWindowOpen(now: Date): boolean {
  const hour = Number(viennaWall(now.getTime()).hour)
  return hour >= FAVORITE_PUSH_FIRST_HOUR && hour < FAVORITE_PUSH_END_HOUR
}

/** When a favourite push can leave next: right away from 08:00 to 21:59, else the next 08:00 in Vienna. */
export function nextFavoritePushTime(now: Date): Date {
  if (favoritePushWindowOpen(now)) return now
  const today = viennaToday(now).date
  const beforeMorning = Number(viennaWall(now.getTime()).hour) < FAVORITE_PUSH_FIRST_HOUR
  const day = beforeMorning ? today : addCalendarDays(today, 1)
  return viennaInstant(day, `${String(FAVORITE_PUSH_FIRST_HOUR).padStart(2, '0')}:00`)
}

/** Whether an action sent at `now` still runs when its push can leave. */
export function actionWillPush(endsAt: Date, now: Date): boolean {
  return endsAt.getTime() > nextFavoritePushTime(now).getTime()
}

const dayFormats = new Map<'de' | 'en', Intl.DateTimeFormat>()

function dayParts(at: Date, locale: 'de' | 'en'): Record<string, string> {
  let format = dayFormats.get(locale)
  if (!format) {
    format = new Intl.DateTimeFormat(locale === 'de' ? 'de-AT' : 'en-GB', {
      timeZone: VIENNA,
      weekday: 'short',
      day: 'numeric',
      month: locale === 'de' ? '2-digit' : 'short',
    })
    dayFormats.set(locale, format)
  }
  return Object.fromEntries(format.formatToParts(at).map((part) => [part.type, part.value]))
}

/**
 * How an action's end reads after "bis": the time when it ends today ("19:00"), else the day it
 * ends on ("So. 18.10." / "Sun 18 Oct"); empty when the end cannot be formatted.
 */
export function actionUntilLabel(endsAt: Date, now: Date, locale: 'de' | 'en'): string {
  try {
    if (viennaToday(endsAt).date === viennaToday(now).date) {
      const p = viennaWall(endsAt.getTime())
      return `${p.hour}:${p.minute}`
    }
    const p = dayParts(endsAt, locale)
    return locale === 'de'
      ? `${p.weekday} ${p.day}.${p.month}.`
      : `${p.weekday} ${p.day} ${p.month}`
  } catch {
    return ''
  }
}
